/**
 * WelcomeAddCard — first-sign-in interstitial that nudges newly invited
 * members to add a card on file before they hit the dashboard.
 *
 * Trigger logic lives in Dashboard.jsx: if user is signed-in + not in
 * athlete mode + has no payment method on file + hasn't skipped the
 * prompt yet, the dashboard redirects here.
 *
 * Two CTAs:
 *  • Add card on file → POST /api/billing/setup-card → Stripe Checkout in
 *    setup mode. Successful Stripe session lands them at
 *    /dashboard/billing?card_added=1 (existing flow).
 *  • Skip for now → POST /api/billing/skip-card-setup which writes
 *    `card_setup_skipped_at` on the user doc. The dashboard then stops
 *    auto-redirecting them here, but the existing Card-on-File banner
 *    on /dashboard/billing still reminds them.
 *
 * Card data NEVER touches our servers — Stripe-hosted Checkout, PCI SAQ-A.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    CreditCard, ShieldCheck, Lock, Loader2, ArrowRight, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WelcomeAddCard() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [skipping, setSkipping] = useState(false);

    const firstName = user?.parent_first_name || (user?.name || "Athlete").split(" ")[0];

    const startStripe = async () => {
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/billing/setup-card`,
                { origin_url: window.location.origin },
                { withCredentials: true },
            );
            // Stripe Checkout will send them to /dashboard/billing?card_added=1
            // on success. Cancel returns to /dashboard/billing — which is fine.
            window.location.href = r.data.url;
        } catch (e) {
            toast.error("Couldn't open Stripe", { description: e?.response?.data?.detail });
            setBusy(false);
        }
    };

    const skip = async () => {
        setSkipping(true);
        try {
            await axios.post(`${API}/billing/skip-card-setup`, {}, { withCredentials: true });
            await refreshUser?.();
            navigate("/dashboard", { replace: true });
        } catch (e) {
            toast.error("Couldn't skip", { description: e?.response?.data?.detail });
            setSkipping(false);
        }
    };

    return (
        <div data-testid="welcome-add-card-page" className="min-h-screen bg-brand-obsidian text-white">
            <div className="relative pt-24 md:pt-32 pb-16 border-b border-zinc-900">
                <div className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none" />
                <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-5 inline-flex items-center gap-3">
                        <Sparkles className="w-3 h-3" />
                        One last step
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-wider leading-[0.95]">
                        WELCOME,
                        <br />
                        <span className="text-brand-red">{firstName.toUpperCase()}.</span>
                    </h1>
                    <p className="mt-6 text-zinc-200 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
                        Let's put a <span className="text-white font-semibold">card on file</span> so your coach can
                        charge sessions, packs, or your monthly membership in 1 tap — no chasing payment, no
                        delays. You won't be charged anything today.
                    </p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-8">
                <div className="border border-zinc-800 rounded-2xl bg-zinc-950/70 p-6 md:p-8 space-y-5">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl border border-brand-red/50 bg-brand-red/10 flex items-center justify-center shrink-0">
                            <CreditCard className="w-6 h-6 text-brand-red" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-display text-xl tracking-wider uppercase">Save a card with Stripe</h2>
                            <p className="text-sm text-zinc-300 mt-1.5 leading-relaxed">
                                Securely vaulted via Stripe Checkout — we never see or store your card number.
                                Same security every Fortune-500 retailer uses.
                            </p>
                        </div>
                    </div>

                    <ul className="grid sm:grid-cols-2 gap-3 text-[12px] text-zinc-300">
                        <li className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                            <span><b>$0 charged today.</b> Just attaches a card to your account.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Lock className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                            <span>PCI-compliant Stripe vault. We never touch your card.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                            <span>Each charge is sent to you for approval first.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Lock className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                            <span>Cancel or update the card any time from your billing page.</span>
                        </li>
                    </ul>

                    <button
                        type="button"
                        data-testid="welcome-add-card-start"
                        onClick={startStripe}
                        disabled={busy || skipping}
                        className="w-full text-sm tracking-[0.15em] uppercase px-6 py-4 border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                        Add card on file
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="text-center">
                    <button
                        type="button"
                        data-testid="welcome-add-card-skip"
                        onClick={skip}
                        disabled={busy || skipping}
                        className="text-[11px] tracking-[0.25em] uppercase text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                    >
                        {skipping && <Loader2 className="w-3 h-3 animate-spin" />}
                        Skip for now — I'll do it later
                    </button>
                    <p className="text-[11px] text-zinc-500 mt-2 max-w-md mx-auto">
                        We'll show a small banner on your billing page to remind you, but you won't see this screen again.
                    </p>
                </div>
            </div>
        </div>
    );
}
